import React, { useCallback, useEffect, useState } from "react";
import { ListEditor } from "../../components/list-editor";
import {
    usePosterSizesQueryQuery,
    useRegionsQueryQuery,
    usePosterSizesMutationMutation,
    PosterSize,
    PosterSizesQueryQuery
} from '../../generated/graphql';
import { variableCurrencyValue } from "../../components/field-table";
import { PosterSizesRegionModal } from './PosterSizesRegionModal';
import {Button, Tooltip} from "@mui/material";
import {Menu} from "@mui/icons-material";
import {PosterSizeDCSelect} from "../../components/MaterialSelects/PosterSizeDCSelect";
import {CurrencyField} from "../../components/CurrencyInput";
import {FieldTableColumnProps} from "../../components/FieldTable/FieldTableTypes";
import {useLocation} from "react-router";
import {FieldTableTitleTooltip} from "../../components/FieldTable/FieldTableTitleTooltip";
import {uniqueId} from "../../utils";
import { useDaybookContext } from '../../contexts/Daybook/DayBookContext';
import { PosterSizeSelect } from "../../components/MaterialSelects/PosterSizeSelect";




const columnsRaw:FieldTableColumnProps<PosterSize>[] = [
    {
        header: '...',
        accessor: 'id',
        dataType: 'select',
        width: 'narrow',
        readOnly: true,
        noSort: true,
        data: {},
        options: {
            // eslint-disable-next-line react/display-name
            renderViewer: ({ row, columnData }) =>
                <Button onClick={() => { columnData.setShowModal(row) }}><Menu/></Button>
        }
    },
    {
        header: 'Name',
        accessor: 'name',
        dataType: 'text',
    }, {
        headerRaw: 'External Name',
        header: <Tooltip title="Name shown to customer"><FieldTableTitleTooltip>External Name</FieldTableTitleTooltip></Tooltip>,
        accessor: 'customer_name',
        dataType: 'text',
    }, {
        headerRaw: 'Area Bookings',
        header: <Tooltip title="Is this poster size selectable for an area booking?"><FieldTableTitleTooltip>Area Bookings</FieldTableTitleTooltip></Tooltip>,
        accessor: 'is_street_poster',
        dataType: 'boolean'
    }, {
        headerRaw: 'Full Size Panel',
        header: <Tooltip title={<>Is this poster size valid for the full size of a Panel?<br />(e.g. UA 4 sheet = no, A 4 sheet = yes)</>}><FieldTableTitleTooltip>Full Size Panel</FieldTableTitleTooltip></Tooltip>,
        accessor: 'is_panel_poster',
        dataType: 'boolean'
    }, {
        headerRaw: 'MediaTypes',
        header: <Tooltip title={<>Should this appear in the dropdown for Media Types poster size?</>}><FieldTableTitleTooltip>Media Types</FieldTableTitleTooltip></Tooltip>,
        accessor: 'is_media_type',
        dataType: 'boolean'
    }, {
        headerRaw: '>= 4 sheet',
        header: <Tooltip title={<>Is this poster size larger than or equal to a 4 sheet?<br />(when ticked, prescribed pricing is used when a full panel of this size is booked)</>}><FieldTableTitleTooltip>4 sheet</FieldTableTitleTooltip></Tooltip>,
        accessor: 'is_larger_than_4',
        dataType: 'boolean'
    }, {
        header: 'DC',
        accessor: 'dc_type',
        dataType: 'select',
        width: '10rem',
        options: {
            // eslint-disable-next-line react/prop-types, react/display-name
            renderViewer: ({ field }) => field.value ?? 'None',
            // eslint-disable-next-line react/prop-types, react/display-name
            renderEditor: ({ field }) => <PosterSizeDCSelect valueIsId={true} {...field} />
        }
    }, {
        headerRaw: 'Slot Poster Type',
        header: <Tooltip title={<>If this size is used in Slot bookings, this is the base type.</>}><FieldTableTitleTooltip>Slot Type</FieldTableTitleTooltip></Tooltip>,
        accessor: 'slotPosterSize',
        sort: 'slotPosterSize.name',
        dataType: 'select',
        filterValue:(v:PosterSize)=>{
            return v?{name:v.name,id:v.id,__typename:v.__typename}:v;
        },
        options: {
            label: 'name',
            renderEditor: ({ field }) => <PosterSizeSelect {...field} autoFocus={true} isPanelPoster={true} allowBlank={true} />
        }
    }, {
        headerRaw: 'Slot Poster Count',
        header: <Tooltip title={<>If this size is used in slot bookings, this is the number of posters</>}><FieldTableTitleTooltip>Slot Count</FieldTableTitleTooltip></Tooltip>,
        accessor: 'slot_poster_count',
        dataType: 'number'
    }, {
        headerRaw: 'Effective Count',
        header: <Tooltip title={<>Used for counting posters for reporting purposes</>}><FieldTableTitleTooltip>Effective Count</FieldTableTitleTooltip></Tooltip>,
        accessor: 'effective_poster_count',
        dataType: 'number'
    }, {
        header: 'Area - London',
        accessor: 'londonPricing',
        dataType: 'currency',
        options: {
            currency: {
              accessor: 'londonCurrencyId',
              currencyValueIsId:true
            }
          }
    }, {
        header: 'Area - Regional',
        accessor: 'regionalPricing',
        dataType: 'currency',
        options: {
            currency: {
              accessor: 'regionalCurrencyId',
              currencyValueIsId:true
            }
        }
    }, {
        header: 'Area - Festival',
        accessor: 'festivalPricing',
        dataType: 'currency',
        options: {
            currency: {
                accessor: 'festivalCurrencyId',
                currencyValueIsId:true
            }
        }
    }, {
        header: 'Area - Intl',
        accessor: 'internationalPricing',
        dataType: 'currency',
        options: {
            currency: {
                accessor: 'internationalCurrencyId',
                currencyValueIsId:true
            }
        }
    }
];


function prepareVariables(variables) {
    if (variables.update) {
        variables.update = variables.update.map((el) => {
            /*if(el.londonCurrency) {
                el.londonCurrencyId = el.londonCurrency.connect;
                delete el.londonCurrency;
            }
            if(el.regionalCurrency) {
                el.regionalCurrencyId = el.regionalCurrency.connect;
                delete el.regionalCurrency;
            }
            if(el.festivalCurrency) {
                el.festivalCurrencyId = el.londonCurrency.connect;
                delete el.festivalCurrency;
            }*/
            delete el.regionTypePricing;
            return el;
        });
    }
    if (variables.create) {
        variables.create = variables.create.map((el) => {
            delete el.regionTypePricing;
            return el;
        });
    }
    return variables;
}

const relationships = {
    region:'connect',
    currency:'connect',
    londonCurrency:'connect',
    regionalCurrency:'connect',
    festivalCurrency:'connect',
    slotPosterSize:'connect'
};

function getValues(data:PosterSizesQueryQuery) {
    return data?.posterSizes??[];
}

function PosterSizes() {
    const daybook = useDaybookContext();
    const regionPricing = daybook?.regionAreaBookingPricing??false;
    const [columns, setColumns] = useState(
        columnsRaw
    );
    const [selectedPosterSize, setSelectedPosterSize] = useState<null | PosterSize>(null);


    const newRow =useCallback(()=> {
        return {
            id: uniqueId(),
            name: '',
            customer_name: '',
            is_street_poster: false,
            is_panel_poster: false,
            is_larger_than_4: false,
            is_media_type: false,
            dc_type: '',
            areaBookingRegionPricing: [],
            regionalCurrencyId:parseInt(daybook?.defaultSiteCurrency?.id??'0'),
            internationalCurrencyId:parseInt(daybook?.defaultSiteCurrency?.id??'0'),
            festivalCurrencyId:parseInt(daybook?.defaultSiteCurrency?.id??'0'),
            londonCurrencyId:parseInt(daybook?.defaultSiteCurrency?.id??'0'),
        }
    },[daybook?.defaultSiteCurrency]);
    


    columns[0].data.setShowModal = useCallback((row) => {
        setSelectedPosterSize(row)
    }, []);

    // hide the [...] icon for regional pricing if it isn't needed
    const hideWhen = useCallback(()=>{
        return !daybook?.regionAreaBookingPricing;
    },[daybook?.regionAreaBookingPricing]);
    columns[0].hideWhen = hideWhen;

    const [regions, setRegions] = useState([]);
    const [shownColumns, setShownColumns] = useState([]);
    const [optionalColumns, setOptionalColumns] = useState([]);
    const regionsQuery = useRegionsQueryQuery({ skip: !regionPricing });

    // optional columns
    useEffect(() => {
        if (!regionsQuery.data) return;
        let idx = 0;
        const cols = regionsQuery?.data?.regions?.data.map((v) => {
            idx++;
            return ({
                header: v.name,
                accessor: 'areaBookingRegionPricing-' + idx,
                className: 'column-currency',
                optional: true,
                data: { id: idx, regionId: v.id },
                dataType: 'currency',
                options: {
                    currencyAccessor: '1',
                    getValue:({ row, columnData, field }) => {
                        let pricing = null, pricingIdx = null;
                        row.areaBookingRegionPricing.forEach((v, k) => {
                            if (v.region.id == columnData.regionId) {
                                pricingIdx = k;
                                pricing = v;
                            }
                        });
                        if (pricing) {
                            return pricing.unit_value;
                        }
                        return 0;
                    },
                    renderViewer: ({ row, columnData, field }) => {
                        let pricing = null, pricingIdx = null;
                        row.areaBookingRegionPricing.forEach((v, k) => {
                            if (v.region.id == columnData.regionId) {
                                pricingIdx = k;
                                pricing = v;
                            }
                        });
                        if (pricing) {
                            return variableCurrencyValue({ currency: pricing.currency ?? field.currency, value: pricing.unit_value, places: 2, qty: 1 });
                        }
                        return '-';
                    },
                    // eslint-disable-next-line react/prop-types, react/display-name
                    renderEditor: ({ field, row, columnData, setValue, rowName }) => {
                        let pricing = null, pricingIdx = null;
                        row.areaBookingRegionPricing.forEach((v, k) => {
                            if (v.region.id == columnData.regionId) {
                                pricingIdx = k;
                                pricing = v;
                            }
                        });
                        const value = pricing ? pricing.unit_value : '0';
                        const currency = pricing ? pricing.currency : null;
                        function onChange(v) {
                            console.log('change',v);
                            //var target = v.target?v.target:v.currentTarget;
                            const isValue = v.target.name.includes('unit_value');
                            if (pricing) {
                                if (isValue) {
                                    setValue(rowName + '.areaBookingRegionPricing[' + pricingIdx + '].unit_value', v.target.value);
                                } else {
                                    setValue(rowName + '.areaBookingRegionPricing[' + pricingIdx + '].currency', v.target.value);
                                }
                            } else {
                                const val = {
                                    id: Math.floor(Date.now()) + '-' + Math.floor(Math.random() * 1000000),
                                    unit_value: (isValue ? v.target.value : '0'),
                                    region: { id: columnData.regionId },
                                    currency: (!isValue ? v.target.value : null)
                                };
                                setValue(rowName + '.areaBookingRegionPricing[' + row.areaBookingRegionPricing.length + ']', val);
                            }
                        }
                        return <CurrencyField {...field} currencySelect={true} currency={currency} currencyAccessor={rowName + '.areaBookingRegionPricing[' + pricingIdx + '].currency'}
                            name={rowName + '.areaBookingRegionPricing[' + pricingIdx + '].area.unit_value'} value={value} onChange={onChange} autoFocus={true} />;
                    }
                }
            });
        });
        setOptionalColumns(cols);
    }, [regionsQuery.data]);

    useEffect(() => {
        // do not run this code until after the server config has loaded
        if(!daybook) return;
        // remove various columns relating to regionPricing, it is assumed that the daybook flag does not change
        if (regionPricing) {
            setColumns(() => {
                const c = [...columns];
                if(false) {
                    c.pop();
                    c.pop();
                    c.pop();
                    c.pop();
                }
                return c;
            })
        }
    }, [daybook,regionPricing]);

    useEffect(() => {
        const newRegions = shownColumns.map((columnId) => {
            return optionalColumns[columnId]?.data?.regionId;
        });
        setRegions(newRegions);
    }, [optionalColumns, shownColumns]);

    const query = usePosterSizesQueryQuery({ variables: { regions: regions } });
    const [mutation,mutationResult] = usePosterSizesMutationMutation({ variables: { regions: regions } });
    const url = useLocation().pathname;
    return (<>
        <ListEditor
            columns={columns}
            relationships={relationships}
            title="Poster Sizes"
            prepareVariables={prepareVariables}
            query={query}
            mutation={mutation}
            shownColumns={daybook?.regionAreaBookingPricing?shownColumns:null}
            optionalColumns={daybook?.regionAreaBookingPricing?optionalColumns:null}
            setShownColumns={setShownColumns}
            maxOptionalColumns={daybook?.regionAreaBookingPricing?6:0}
            permission="postersizes"
            newRow={newRow}
            newRowLabel="Add Poster Size"
            baseUrl={url}
            getValues={getValues}
            saveResult={mutationResult}
            className="poster-sizes"
        />
        <PosterSizesRegionModal posterSize={selectedPosterSize} close={() => { setSelectedPosterSize(null) }} /></>);
}
export default PosterSizes;
